import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosProgressEvent,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import Cookie from "@/utils/cookie";
import C from "@/utils/constants";
import router from "@/router";
import type {
  AuditLogPageResponse,
  CellGroupsPostPayload,
  CellGroupsResponse,
  DeleteTaskResponse,
  PresignResponse,
  SlideCountResponse,
  SlideImageBoundingBoxes,
  SlideReportEntry,
  SlideReportResponse,
  TaskPagedResponse,
  TaskSummary,
  TaskUpdatePayload,
  TextReportResponse,
  RBCBackendPayload,
  RBCDashboardResponse,
} from "@/types/api";

const utils = {
  getUserId(): string | null {
    return Cookie.getCookie(C.COOKIES.KEY.USER_ID);
  },
  async uploadImageToBlob(
    uploadUrl: string,
    file: Blob | File,
    onProcess?: (event: AxiosProgressEvent | ProgressEvent) => void,
  ) {
    return axios.put(uploadUrl, file, {
      headers: {
        [C.HEADER.X_MS_BLOB_TYPE]: "BlockBlob",
        "content-type": "", // if use default content type, upload fail
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProcess) {
          onProcess(progressEvent);
        }
      },
    });
  },
  getProject(): string | null {
    return Cookie.getCookie(C.COOKIES.KEY.PROJECT_NAME);
  },
  getProjectId(): string | null {
    return Cookie.getCookie(C.COOKIES.KEY.PROJECT_ID);
  },
};

const API_BASE = import.meta.env.VITE_ANNOTATION_API || "";

const instance: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

const ensureHeaders = (
  headers?: AxiosRequestHeaders | Record<string, string>,
): AxiosRequestHeaders => (headers as AxiosRequestHeaders) ?? ({} as AxiosRequestHeaders);

const addBearerToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const headers = ensureHeaders(config.headers);
  if (!headers.Authorization) {
    const token = Cookie.getCookie(C.COOKIES.KEY.USER_TOKEN);
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  config.headers = headers;
  return config;
};

const addUserIdParam = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const params = (config.params ?? {}) as Record<string, unknown>;
  params.user_id = utils.getUserId();
  config.params = params;
  return config;
};

const addProjectParam = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const params = (config.params ?? {}) as Record<string, unknown>;
  params.project_id = utils.getProjectId() || utils.getProject();
  config.params = params;
  return config;
};

instance.interceptors.request.use(addBearerToken, (error) => Promise.reject(error));
instance.interceptors.request.use(addUserIdParam);
instance.interceptors.request.use(addProjectParam);

function fulfilled(response: AxiosResponse) {
  return response;
}

function rejected(error: AxiosError) {
  // Check for 401 Unauthorized -- avoid external dependency on http-status-codes
  const status = error?.response?.status;
  if (status === 401) {
    Cookie.deleteCookie(C.COOKIES.KEY.USER_TOKEN);
    // `currentRoute` is a ref in Vue Router 4
    const currentPath = router.currentRoute?.value?.path ?? null;
    if (currentPath !== C.ROUTES.LOGIN) {
      router.push(C.ROUTES.LOGIN);
    }
  }
  return Promise.reject(error);
}

// Register the named functions directly so tests that inspect
// handler.fulfilled.name === 'fulfilled' find the expected name.
instance.interceptors.response.use(fulfilled, rejected);

const userLogin = async (user: { username: string; password: string; project_name?: string }) => {
  try {
    const body = {
      username: user.username,
      password: user.password,
      project_name: user.project_name,
    };
    const resp = await instance.post("/login", body, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return resp.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

// For dev
const processUrl = (presignUrl: string) => presignUrl.replace("blade-blob:9000", "localhost:8080");

const uploadImage = async (
  file: File,
  fileName?: string,
  onProcess?: (event: AxiosProgressEvent | ProgressEvent) => void,
) => {
  const response = await instance.get<PresignResponse>(`${C.ENDPOINT.SLIDES}/presign_url`);
  const uploadUrl = processUrl(response.data.presign_url);
  const imagePath = response.data.image_path;
  let fName = fileName;
  await utils.uploadImageToBlob(uploadUrl, file, onProcess);
  if (!fName) {
    fName = file.name;
  }
  return instance.post(C.ENDPOINT.SLIDES, {
    name: fName,
    img_path: imagePath,
  });
};

interface GetSlidesParams {
  cursor?: string;
  page_size?: number;
  name?: string;
  assignee?: string;
  state?: string;
}

const getSlides = async (params?: GetSlidesParams): Promise<TaskPagedResponse> => {
  const resp = await instance.get<TaskPagedResponse>(C.ENDPOINT.TASKS, { params });
  return resp.data;
};

const updateTask = async (
  taskId: string | number,
  task: TaskUpdatePayload,
): Promise<TaskSummary> => {
  const slides = await instance.put<TaskSummary>(`${C.ENDPOINT.TASKS}/${taskId}`, task);
  return slides.data;
};

const getSlidesCount = async (): Promise<number> => {
  const count = await instance.get<SlideCountResponse>(C.ENDPOINT.SLIDES_COUNT);
  return count.data.total;
};

const getSlideImageBoundingBoxes = async (
  slideId: string | number,
): Promise<SlideImageBoundingBoxes> => {
  const resp = await instance.get<SlideImageBoundingBoxes>(`${C.ENDPOINT.SLIDES}/${slideId}/image`);
  return resp.data;
};

const makeSlideViewHeader = (): Record<string, string> => {
  const token = Cookie.getCookie(C.COOKIES.KEY.USER_TOKEN);
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
};

const getCellGroups = async (slideId: string | number): Promise<CellGroupsResponse["cells"]> => {
  const cellInfo = await instance.get<CellGroupsResponse>(`${C.ENDPOINT.TASKS}/${slideId}/cells`);
  return cellInfo.data.cells;
};

const getRBCImages = async (
  slideId: string | number,
): Promise<RBCBackendPayload> => {
  const resp = await instance.get<RBCBackendPayload>(
    `${C.ENDPOINT.TASKS}/${slideId}/rbc-images`,
  );
  return resp.data;
};

const getCellUrls = (imgPath: string) => {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const token = Cookie.getCookie(C.COOKIES.KEY.USER_TOKEN);
  const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
  return `${base}/image?path=${encodeURIComponent(imgPath)}${tokenParam}`;
};

const getRBCImageUrl = (imgPath: string) => {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const token = Cookie.getCookie(C.COOKIES.KEY.USER_TOKEN);
  const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
  return `${base}/tasks/rbc-image?path=${encodeURIComponent(imgPath)}${tokenParam}`;
};

const getSlideReport = async (slideId: string | number): Promise<SlideReportEntry[]> => {
  const report = await instance.get<SlideReportResponse>(`${C.ENDPOINT.SLIDES}/${slideId}/report`);
  return report.data.report;
};

const postCells = async (slideId: string | number, cellGroupPostPayload: CellGroupsPostPayload) =>
  instance.post(`${C.ENDPOINT.TASKS}/${slideId}/cells`, cellGroupPostPayload);

const genReport = async (cellData: CellGroupsResponse) => {
  const resp = await instance.post<SlideReportResponse>(C.ENDPOINT.GEN_REPORT, cellData);
  return resp.data;
};

const deleteTask = async (taskId: string | number) => {
  const resp = await instance.delete<DeleteTaskResponse>(`${C.ENDPOINT.TASKS}/${taskId}`);
  return resp.data;
};

const getActiveUsers = async (): Promise<string[]> => {
  const resp = await instance.get<{ users: string[] }>(C.ENDPOINT.ACTIVE_USERS);
  return resp.data.users;
};

const getTextReport = async (slideId: string | number): Promise<string> => {
  const resp = await instance.get<TextReportResponse>(
    `${C.ENDPOINT.SLIDES}/${slideId}/review-text`,
  );
  return resp.data.review_text;
};

export default {
  instance,
  utils,
  userLogin,
  uploadImage,
  getSlides,
  getSlidesCount,
  getCellUrls,
  getRBCImageUrl,
  getCellGroups,
  getRBCImages,
  processUrl,
  getSlideReport,
  postCells,
  genReport,
  getSlideImageBoundingBoxes,
  makeSlideViewHeader,
  getTextReport,
  getActiveUsers,
  updateTask,
  deleteTask,
  getAuditTrail: async (page: number = 0): Promise<AuditLogPageResponse> => {
    const resp = await instance.get<AuditLogPageResponse>(C.ENDPOINT.AUDIT, { params: { page } });
    return resp.data;
  },
  getRBCDashboard: async (projectId: string): Promise<RBCDashboardResponse> => {
    const resp = await instance.get(`/rbc/${projectId}/dashboard`);
    return resp.data;
  },
  triggerAggregation: async (projectId: string): Promise<{ status: string; report_id: string }> => {
    const resp = await instance.post(`/rbc/${projectId}/aggregate`);
    return resp.data;
  },
};
